import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDocumentDuTableau } from '../features/cours/document';
import {
  SurfaceDeTrace,
  identiteDe,
  type CommandesDuTableau,
  type ElementDeTableau,
  type ImageDeTableau,
  type PenseBeteDeTableau,
} from '../features/cours/moteur';
import { useScene } from '../features/cours/relecture';
import { lireLErreur } from '../shared/api/client';
import { Bandeau } from '../shared/ui/Bandeau';
import { Bouton } from '../shared/ui/Bouton';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { Squelette } from '../shared/ui/Squelette';
import { Statut } from '../shared/ui/Statut';
import { Titre } from '../shared/ui/Titre';
import { nomAffiche } from '../shared/format/designation';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA RELECTURE D'UN COURS PASSÉ — `CDC-4` F-47, EN LECTURE STRICTE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Elle s'ouvre dans **la même surface que le tableau d'un cours vivant**, en lecture
 * stricte, **et non dans une fenêtre à part** : deux surfaces feraient deux fois le même
 * écran. » C'est pourquoi cette page monte le **même** `Canevas` que `PageCours`, avec
 * `gele`, plutôt qu'un rendu qui lui ressemblerait — deux peintres du même tracé
 * divergeraient au premier changement du moteur.
 *
 * **ON ARRIVE ICI PAR L'HISTORIQUE, ET PAR NULLE PART AILLEURS** : « on ouvre la relecture
 * depuis la ligne du cours dans l'historique, et c'est **le seul chemin pour un
 * participant** ». Aucun lien n'y mène depuis l'écran d'arrivée ni depuis un cours vivant.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA LISTE FERMÉE DE QUATRE GESTES, ET CE QU'ELLE N'EXCLUT PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « Regarder, **désigner un tracé pour voir son auteur**, circuler entre les tableaux,
 * fabriquer le document. **Ni écrire, ni effacer, ni copier.** »
 *
 * Et la précision de Q547, qui évite une lecture trop large : « cette liste fermée porte
 * sur ce qu'on fait **AU TRACÉ**, et elle **n'exclut aucune surface flottante** posée
 * par-dessus. **La bulle de conversations est présente en relecture comme partout
 * ailleurs.** La lecture stricte reste stricte **sur le tracé**. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CET ÉCRAN NE MONTRE PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucune barre d'outils.** Il n'y a rien à tracer, donc rien à choisir.
 * - **Aucune jauge de plafond.** Une scène figée ne grossit plus.
 * - **Aucune date d'effacement.** `effacementPrevuLe` existe au contrat et **cet écran ne
 *   l'affiche pas** : « la durée de relecture est un réglage de structure **qu'une personne
 *   ne connaît pas**, et lui donner une date lui ferait lire ce réglage » (F-46).
 * - **Aucun curseur de personne.** Personne n'est là : la relecture « est anonyme et ne
 *   diffuse rien ».
 */
export function PageRelecture() {
  const { coursId } = useParams<{ coursId: string }>();
  const navigate = useNavigate();
  const [rang, poserRang] = useState(1);
  const scene = useScene(coursId ?? '', rang);
  /**
   * **Les commandes du moteur, pour la fabrication du document.** Même mécanique que
   * sur le cours vivant : ce sont **des fonctions**, jamais l'API du moteur.
   */
  const [commandes, poserLesCommandes] = useState<CommandesDuTableau | null>(
    null,
  );

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **F-47 — LE DEUXIÈME GESTE DE LA LISTE FERMÉE, ET IL N'ÉTAIT SERVI PAR
   * AUCUNE SURFACE.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * « Ce qu'on y fait, liste fermée de quatre gestes : regarder, **désigner un tracé
   * pour voir son auteur**, circuler entre les tableaux, fabriquer le document. » Et
   * F-26 porte la règle : « la relecture montre **l'auteur de chaque tracé**, à tout le
   * monde qui relit » (Q376).
   *
   * **Ce qui manquait n'était pas la donnée.** La réponse de la scène porte
   * `auteurPrenom` sur les trois familles d'éléments depuis toujours ; ce qu'aucun écran
   * ne savait était **lequel des éléments la personne vient de toucher**, question à
   * laquelle seul le moteur peut répondre. Le port la rend depuis le 20260829, et il ne
   * rend **qu'une identité** — voir sa prop `onDesignation` pour ce que cela coûte à
   * D-012, et pourquoi cela ne l'amende pas.
   *
   * **L'identité désignée ne se garde que pour un tableau** : circuler en change la
   * scène, donc l'élément désigné n'y est plus.
   */
  const [designe, poserDesigne] = useState<string | null>(null);

  const allerAuTableau = (rangVoulu: number) => {
    poserDesigne(null);
    poserRang(rangVoulu);
  };

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **F-47 — LE QUATRIÈME GESTE DE LA LISTE FERMÉE, ET IL MANQUAIT.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * « Ce qu'on y fait, **liste fermée de quatre gestes** : regarder, désigner un tracé
   * pour voir son auteur, circuler entre les tableaux, **fabriquer le document**. »
   *
   * **Cet écran ne pouvait pas le servir, et la cause était au contrat** : constaté au
   * commit `ca45270`, `relireUneScene` ne rendait **pas** le `tableauId`, et la route de
   * fabrication s'adresse par lui. Elle le rend depuis le 20260829.
   *
   * **Le geste est ouvert à tout le monde ici aussi** : « le droit de fabriquer suit le
   * droit de relire, **sans ligne à lui** » (Q370) — et qui est sur cet écran a le droit
   * de relire, le serveur l'ayant déjà accordé.
   */
  const documentDuTableau = useDocumentDuTableau(scene.data?.tableauId ?? '');

  const elements: readonly ElementDeTableau[] = useMemo(
    () =>
      (scene.data?.traces ?? []).map(
        (trace) => trace.contenu as ElementDeTableau,
      ),
    [scene.data],
  );

  /** **Les images de la scène, peintes par le port depuis leur adresse signée.** */
  const imagesEnPlace: readonly ImageDeTableau[] = useMemo(
    () =>
      (scene.data?.images ?? []).map((image) => ({
        id: image.id,
        adresse: image.adresse,
        positionX: image.positionX,
        positionY: image.positionY,
        largeur: image.largeur,
        hauteur: image.hauteur,
      })),
    [scene.data],
  );

  /**
   * **Les pense-bêtes se peignent SUR la scène, et non sous elle.**
   *
   * Ils étaient listés en dessous du tableau, faute d'une couleur et d'une position au
   * modèle : « le canevas ne les peint pas — leur forme n'est pas un tracé ». Le modèle
   * les porte depuis le 20260829, donc ils reprennent leur place. **La liste d'auteurs
   * reste**, et elle n'est pas un doublon : voir son commentaire.
   */
  const penseBetesEnPlace: readonly PenseBeteDeTableau[] = useMemo(
    () =>
      (scene.data?.penseBetes ?? []).map((note) => ({
        id: note.id,
        texte: note.texte,
        couleur: note.couleur,
        positionX: note.positionX,
        positionY: note.positionY,
      })),
    [scene.data],
  );

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **QUI A POSÉ QUOI, PAR IDENTITÉ D'ÉLÉMENT — ET LES TROIS FAMILLES Y SONT.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **La clé est celle que le port rend.** Pour un tracé, c'est l'identité de naissance
   * de l'élément (D-121), lue par `identiteDe` — la seule chose que le port laisse lire
   * d'un élément opaque. Pour une image et pour un pense-bête, c'est l'identité du
   * serveur, celle-là même que le port leur pose sur la scène.
   *
   * **Les trois familles entrent, et ce n'est pas une règle inventée** : la réponse de
   * la scène porte `auteurPrenom` sur les trois, et une image posée par quelqu'un a un
   * auteur au même titre qu'un trait. Le geste que F-47 nomme est le tracé ; les deux
   * autres n'exigeaient pas une ligne de plus.
   *
   * **UN AUTEUR ABSENT EST UNE VALEUR, PAS UN TROU** : `auteurPrenom` est vide quand le
   * compte a été anonymisé, et « à l'anonymisation, **le tracé reste sans nom** »
   * (Q377). La table garde donc la clé avec une valeur vide, ce qui distingue
   * « anonymisé » de « pas dans cette scène » — et l'écran ne masque ni n'invente.
   */
  const auteurParElement = useMemo(() => {
    const table = new Map<string, string | null>();
    for (const trace of scene.data?.traces ?? []) {
      table.set(identiteDe(trace.contenu as ElementDeTableau), trace.auteurPrenom);
    }
    for (const image of scene.data?.images ?? []) {
      table.set(image.id, image.auteurPrenom);
    }
    for (const note of scene.data?.penseBetes ?? []) {
      table.set(note.id, note.auteurPrenom);
    }
    return table;
  }, [scene.data]);

  if (coursId === undefined) return null;

  const nombre = scene.data?.nombreDeTableaux ?? 1;

  /**
   * **Ce que dit la ligne d'auteur, dans les trois cas et jamais dans un quatrième.**
   *
   * Rien de désigné : le geste s'annonce, sinon rien ne le ferait deviner — le moteur
   * n'entoure aucun élément en lecture stricte, il efface sa sélection à chaque
   * relâchement. Un auteur connu : son prénom. **Un auteur anonymisé : « sans nom »**,
   * exactement le mot de F-47 et de Q377 — une **absence**, et non une cause : F-46
   * règle 2 veut « une désignation anonyme neutre, **sans cause et sans date** ».
   */
  const auteurDesigne =
    designe === null ? undefined : auteurParElement.get(designe);
  const ligneDAuteur =
    auteurDesigne === undefined
      ? 'Touchez un tracé pour voir qui l’a posé.'
      : auteurDesigne === null
        ? 'Tracé sans nom.'
        : `Tracé de ${auteurDesigne}.`;

  return (
    <div className="flex h-dvh flex-col gap-3 bg-tableau-travail p-3">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <span className="flex flex-wrap items-center gap-4">
          <Titre niveau={1} taille="18">
            Cours passé
          </Titre>
          {/* « La surface montre **le rang du tableau courant et le nombre de tableaux du
              cours**, et on circule de l'un à l'autre. » */}
          <Statut teinte="neutre" pleine={false}>
            {`Tableau ${rang} sur ${nombre}`}
          </Statut>
          <Statut teinte="eteint">Lecture seule</Statut>

          {/* ═══════════════════════════════════════════════════════════════════
              **L'AUTEUR SE LIT DANS L'EN-TÊTE, ET NON DANS UNE SURFACE POSÉE
              PAR-DESSUS LE TABLEAU.**
              ═══════════════════════════════════════════════════════════════════

              **Deux décisions du dépôt disent ce que coûte un bloc flottant au-dessus du
              tracé, et les deux ont été trouvées à l'œil.** D-105 : le sélecteur de
              structure était recouvert par le menu, « un motif juste dans l'arbre du
              document et faux à l'écran ». D-113, défaut 3 : le panneau de visio flottait
              « en haut à droite, au motif de `DESIGN.md` que tout ce qui n'est pas le
              tracé prend de la place sur le tracé » — et il se posait exactement sur
              « Quitter » et « Terminer le cours ». **Le tuteur ne pouvait plus terminer
              son cours**, et « le contrôle de recouvrement n'existe pas dans ce dépôt ».

              **Ici, la surface flottante n'est pas seulement risquée, elle est inutile.**
              Une bulle posée à l'endroit du tracé désigné recouvrirait **précisément ce
              qu'on vient de désigner**. L'en-tête est dans le flux, il existe déjà, et il
              porte le rang du tableau : il ne recouvre rien et ne prend pas un point de
              tracé de plus.

              **Ce que Q547 n'autorise pas ici.** « La liste fermée n'exclut aucune surface
              flottante posée par-dessus » — c'est ce qui laisse la bulle de conversations
              en relecture. Cette phrase **permet** une surface flottante, elle n'en
              **demande** aucune, et les deux décisions ci-dessus disent laquelle choisir
              quand on a le choix.

              **`aria-live` parce qu'un texte qui change tout seul ne se relit pas** : la
              désignation se fait sur le canevas, l'auteur paraît ici, et rien ne
              signalerait le changement à qui n'a pas les yeux dessus. */}
          <span
            aria-live="polite"
            className="text-13 text-texte-faible"
          >
            {ligneDAuteur}
          </span>
        </span>

        <span className="flex items-center gap-2">
          <Bouton
            variante="douce"
            taille="petite"
            disabled={rang <= 1}
            onClick={() => allerAuTableau(rang - 1)}
          >
            Tableau précédent
          </Bouton>
          <Bouton
            variante="douce"
            taille="petite"
            disabled={rang >= nombre}
            onClick={() => allerAuTableau(rang + 1)}
          >
            Tableau suivant
          </Bouton>
          {/* **F-47, quatrième geste de la liste fermée.** Il attend deux choses : la
              scène, qui porte le tableau auquel la route s'adresse, et le moteur, qui
              dessine les cases. */}
          <Bouton
            variante="douce"
            taille="petite"
            enCours={documentDuTableau.isPending}
            disabled={commandes === null || scene.data === undefined}
            onClick={() => {
              if (commandes === null) return;
              documentDuTableau.mutate(commandes.exporterLesPages);
            }}
          >
            Fabriquer le document
          </Bouton>
          <Bouton
            variante="fantome"
            taille="petite"
            onClick={() => void navigate('/historique')}
          >
            Revenir à l’historique
          </Bouton>
        </span>
      </header>

      {scene.isLoading && <Squelette lignes={3} />}

      {scene.isError && (
        // **Les deux refus se disent différemment**, et aucun des deux ne porte de date.
        <>
          {lireLErreur(scene.error).code === 'TABLEAU_SANS_TRACE' ? (
            <Bandeau ton="attention">
              Ce tableau ne porte aucun tracé. Un cours peut finir sur un tableau
              neuf que personne n’a écrit.
            </Bandeau>
          ) : lireLErreur(scene.error).code === 'SCENE_EFFACEE' ? (
            <Bandeau ton="echec">
              Le contenu de ce cours n’est plus consultable.
            </Bandeau>
          ) : (
            <EtatErreur
              message={lireLErreur(scene.error).message}
              onReessayer={() => void scene.refetch()}
            />
          )}
        </>
      )}

      {scene.data !== undefined && (
        <>
          <SurfaceDeTrace
            elementsEnPlace={elements}
            // Rien n'arrive en direct : la relecture est un état figé.
            elementsRecus={[]}
            // Personne n'est là. La relecture « est anonyme et ne diffuse rien ».
            curseurs={[]}
            imagesEnPlace={imagesEnPlace}
            penseBetesEnPlace={penseBetesEnPlace}
            outil="selection"
            encre="noir"
            epaisseur={3}
            // **`gele` DISAIT LA MAUVAISE CHOSE, ET IL EST REMPLACÉ.** Il porte une
            // seule règle — « au plafond, le cours est gelé en écriture » (F-21) —, et
            // l'employer ici disait à quelqu'un qui relit une archive que **son tableau
            // est plein**. La lecture stricte de F-26 et de F-47 est une autre règle :
            // elle ferme l'écriture ET le pointeur, « la relecture ne diffuse rien »
            // (DD-12). Le port les distingue depuis le 20260829.
            gele={false}
            lectureStricte
            onPose={() => undefined}
            onRetrait={() => undefined}
            onPointeur={() => undefined}
            // **F-47, deuxième geste de la liste fermée.** Le port rend l'identité de
            // l'élément touché, et rien d'autre ; l'auteur se lit dans la table
            // ci-dessus, que la réponse de la scène porte déjà.
            onDesignation={poserDesigne}
            onPret={poserLesCommandes}
          />

          {documentDuTableau.isError && (
            <Bandeau ton="echec">
              Ce document n’a pas pu être fabriqué. Réessayez.
            </Bandeau>
          )}
          {documentDuTableau.data !== undefined &&
            documentDuTableau.data.pages === 0 && (
              <Bandeau ton="attention">
                Ce cours n’a aucune case écrite : il n’y a pas de document à
                fabriquer.
              </Bandeau>
            )}

          {/* ═══════════════════════════════════════════════════════════════════
              **LES AUTEURS DES PENSE-BÊTES, ET CE N'EST PAS UN DOUBLON.**
              ═══════════════════════════════════════════════════════════════════

              Les pense-bêtes eux-mêmes sont **sur** la scène depuis le 20260829, à leur
              place et de leur couleur. Ce qui reste ici est **l'auteur**, que le canevas
              ne porte nulle part : « la relecture montre **l'auteur de chaque tracé**, à
              tout le monde qui relit » (Q376), et « à l'anonymisation, le tracé reste
              sans nom » (Q377).

              **LE GESTE QUE F-47 NOMME EST SERVI DEPUIS LE 20260829, ET CETTE LISTE NE
              LE DOUBLE PAS.** « Désigner un tracé pour voir son auteur » se lit dans
              l'en-tête, sur l'élément touché, les trois familles comprises. **Cette
              liste-ci reste** parce qu'elle ne sert pas la même chose : elle porte **le
              texte** d'un pense-bête à côté de son auteur, là où la désignation demande
              de savoir lequel toucher. Un pense-bête peut être sous un autre, ou hors de
              la vue. */}
          {scene.data.penseBetes.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {scene.data.penseBetes.map((note) => (
                <li
                  key={note.id}
                  className="max-w-etroite rounded-3 bg-fond-alerte p-3 text-14 text-texte"
                >
                  {note.texte}
                  {/* « **L'auteur de chaque tracé est montré**, et laissé sans nom après
                      anonymisation. »

                      **LE MOT VIENT DE `shared/format/designation.ts`, ET D'UN SEUL
                      ENDROIT.** Il disait « Compte supprimé » jusqu'au 20260830, dans sept
                      fichiers de `web/` — donc **une CAUSE**, là où F-46 règle 2 demande
                      « une désignation anonyme neutre, **sans cause et sans date** ». Les
                      sept ont changé ensemble : les corriger un par un aurait fait **deux
                      vocabulaires pour un même état**, ce qui est pire qu'un seul mot
                      discutable. Branche (B) de D-135.

                      **La ligne d'auteur de l'en-tête dit « Tracé sans nom »**, et c'est
                      une désignation de TRACÉ, pas de personne : elle reste distincte. */}
                  <span className="mt-1 block text-12 text-texte-faible">
                    {nomAffiche(note.auteurPrenom)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
